import { runtimeConfig } from "@/runtime-config";
import { ReactSetStateFunction } from "@/types/types";
import { useEffect, useState } from "react";

interface UseCheckWhatsNew {
  /** Indicates whether there are new articles available in the "What's New" section */
  newArticle?: string;

  /** Function to manually set the new article state */
  setNewArticle: ReactSetStateFunction<string | undefined>;
}

type NewArticleResponse = {
  /** Base64 encoded url of the latest article */
  latestArticle: string;

  /** Indicates if the data was retrieved from cache */
  isCached?: boolean;
};

/** Key used to store the latest article identifier in local storage */
export const LATEST_WHATS_NEW_ARTICLE_KEY = "latestWhatsNewArticle";

/**
 * Hook to check for new articles in the "What's New" section.
 * It fetch the base64 encoded URL of the latest article and compares it with a stored value in local storage.
 * If they differ or value is not available in local storage, it indicates that there are new articles available.
 *
 * @returns An object containing the latest article identifier and a function to update it.
 */
export function useCheckWhatsNew(): UseCheckWhatsNew {
  const [newArticle, setNewArticle] = useState<string>();

  // Fetch base64 encoded url of the latest article
  useEffect(() => {
    async function fetchLatestArticle(): Promise<void> {
      try {
        const response = await fetch(
          `${runtimeConfig.backendEndpoints.stellarApiUrl}/whats-new`,
        );

        if (!response.ok) {
          return;
        }

        const data: NewArticleResponse = await response.json();

        if (!data.latestArticle) {
          return;
        }

        const { latestArticle } = data;
        const storedArticle = localStorage.getItem(
          LATEST_WHATS_NEW_ARTICLE_KEY,
        );

        if (storedArticle !== latestArticle) {
          setNewArticle(latestArticle);
        }
      } catch {
        // Skip error silently
      }
    }

    fetchLatestArticle();
  }, []);

  return {
    newArticle,
    setNewArticle,
  };
}
